import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';

import { Icons } from '@ohif/ui-next';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover/Popover';
import { Button } from '../Button/Button';
import { Numeric } from '../Numeric/Numeric';
import { Slider } from '../Slider/Slider';

export type CinePlayerProps = {
  className: string;
  isPlaying: boolean;
  minFrameRate?: number;
  maxFrameRate?: number;
  stepFrameRate?: number;
  frameRate?: number;
  onFrameRateChange: (value: number) => void;
  onPlayPauseChange: (value: boolean) => void;
  onClose: () => void;
  updateDynamicInfo?: (info: any) => void;
  dynamicInfo?: {
    dimensionGroupNumber: number;
    numDimensionGroups: number;
    label?: string;
  };
  /** Total number of frames in the stack. When provided (and > 1) the player
   * renders as a docked, YouTube-style bar with a frame timeline/scrubber. */
  frameCount?: number;
  /** 0-based index of the currently displayed frame. */
  currentFrame?: number;
  /** Called when the user drags the frame timeline/scrubber. */
  onFrameChange?: (frame: number) => void;
  /** Whether to render the close (X) button. Defaults to true. */
  showCloseButton?: boolean;
};

const CinePlayer: React.FC<CinePlayerProps> = ({
  className,
  isPlaying = false,
  minFrameRate = 1,
  maxFrameRate = 90,
  stepFrameRate = 1,
  frameRate: defaultFrameRate = 24,
  onFrameRateChange = () => {},
  onPlayPauseChange = () => {},
  onClose = () => {},
  dynamicInfo = {},
  updateDynamicInfo,
  frameCount,
  currentFrame = 0,
  onFrameChange,
  showCloseButton = true,
}) => {
  const isDynamic = !!dynamicInfo?.numDimensionGroups;
  const isScrubberMode = !isDynamic && typeof frameCount === 'number' && frameCount > 1;
  const [frameRate, setFrameRate] = useState(defaultFrameRate);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const debouncedSetFrameRate = useCallback(debounce(onFrameRateChange, 100), [onFrameRateChange]);

  const getPlayPauseIconName = () => (isPlaying ? 'icon-pause' : 'icon-play');

  const handleSetFrameRate = (frameRate: number) => {
    if (frameRate < minFrameRate || frameRate > maxFrameRate) {
      return;
    }
    setFrameRate(frameRate);
    debouncedSetFrameRate(frameRate);
  };

  useEffect(() => {
    setFrameRate(defaultFrameRate);
  }, [defaultFrameRate]);

  const handleDimensionGroupNumberChange = useCallback(
    (newGroupNumber: number) => {
      if (isDynamic && dynamicInfo) {
        updateDynamicInfo?.({
          ...dynamicInfo,
          dimensionGroupNumber: newGroupNumber,
        });
      }
    },
    [isDynamic, dynamicInfo, updateDynamicInfo]
  );

  const playPauseButton = (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => onPlayPauseChange(!isPlaying)}
      data-cy={'cine-player-play-pause'}
    >
      <Icons.ByName name={getPlayPauseIconName()} />
    </Button>
  );

  const frameRateControl = (
    <div>
      <Popover
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
      >
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="h-full border-none bg-transparent p-0 hover:bg-transparent"
          >
            <Numeric.Container
              mode="stepper"
              min={minFrameRate}
              max={maxFrameRate}
              step={stepFrameRate}
              value={frameRate}
              onChange={val => handleSetFrameRate(val as number)}
              className="border-0 bg-transparent"
            >
              <Numeric.NumberStepper
                direction="horizontal"
                inputWidth="w-7 max-w-7"
              >
                <div className="flex items-center justify-center gap-1">
                  <div className="text-foreground flex-shrink-0 text-center text-sm leading-[22px]">
                    <span className="text-muted-foreground whitespace-nowrap text-xs">{' FPS'}</span>
                  </div>
                </div>
              </Numeric.NumberStepper>
            </Numeric.Container>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="center"
          className="cine-fps-range-popover z-50 w-auto p-2"
          sideOffset={8}
        >
          <Numeric.Container
            mode="singleRange"
            min={minFrameRate}
            max={maxFrameRate}
            step={stepFrameRate}
            value={frameRate}
            onChange={val => handleSetFrameRate(val as number)}
            className="h-6 px-2"
          >
            <Numeric.SingleRange
              showNumberInput={false}
              sliderClassName="w-40"
            />
          </Numeric.Container>
        </PopoverContent>
      </Popover>
    </div>
  );

  const closeButton = showCloseButton && (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClose}
      data-cy={'cine-player-close'}
    >
      <Icons.Close />
    </Button>
  );

  if (isScrubberMode) {
    const lastFrame = frameCount - 1;
    const safeCurrent = Math.min(Math.max(currentFrame, 0), lastFrame);

    // In scrubber mode the caller owns positioning via `className` (the docked
    // bar is placed by the cornerstone driver), so we don't hardcode top/left here.
    return (
      <div className={`pointer-events-none z-50 ${className}`}>
        <div className="bg-muted pointer-events-auto flex w-full select-none items-center gap-3 rounded-md px-3 py-2 shadow-lg">
          {playPauseButton}

          <div
            className="flex flex-1 items-center"
            data-cy="cine-player-scrubber"
          >
            <Slider
              className="flex-1"
              value={[safeCurrent]}
              min={0}
              max={lastFrame}
              step={1}
              onValueChange={val => onFrameChange?.(val[0])}
            />
          </div>

          <span className="text-muted-foreground shrink-0 whitespace-nowrap text-xs tabular-nums">
            {safeCurrent + 1} / {frameCount}
          </span>

          {frameRateControl}
          {closeButton}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`pointer-events-none absolute top-10 left-1/2 z-50 -translate-x-1/2 ${className}`}
    >
      <div
        className={
          'bg-muted pointer-events-auto inline-flex select-none items-center gap-2 rounded-md px-2 py-2'
        }
      >
        {playPauseButton}

        {isDynamic && dynamicInfo && (
          <div className="min-w-16 max-w-44 text-foreground flex flex-col">
            <div className="text-xs">
              <span className="text-foreground w-2">{dynamicInfo.dimensionGroupNumber}</span>{' '}
              <span className="text-muted-foreground">{`/${dynamicInfo.numDimensionGroups}`}</span>
            </div>
            <div className="text-muted-foreground text-xs">{dynamicInfo.label}</div>
          </div>
        )}

        {frameRateControl}

        {closeButton}
      </div>

      {isDynamic && dynamicInfo && (
        <Numeric.Container
          mode="singleRange"
          min={1}
          max={dynamicInfo.numDimensionGroups}
          step={1}
          value={dynamicInfo.dimensionGroupNumber}
          onChange={val => handleDimensionGroupNumberChange(val as number)}
          className="pointer-events-auto mt-3 w-full"
        >
          <Numeric.SingleRange showNumberInput={false} />
        </Numeric.Container>
      )}
    </div>
  );
};

CinePlayer.propTypes = {
  /** Minimum value for range slider */
  minFrameRate: PropTypes.number,
  /** Maximum value for range slider */
  maxFrameRate: PropTypes.number,
  /** Increment range slider can "step" in either direction */
  stepFrameRate: PropTypes.number,
  frameRate: PropTypes.number,
  /** 'true' if playing, 'false' if paused */
  isPlaying: PropTypes.bool.isRequired,
  onPlayPauseChange: PropTypes.func,
  onFrameRateChange: PropTypes.func,
  onClose: PropTypes.func,
  isDynamic: PropTypes.bool,
  dynamicInfo: PropTypes.shape({
    dimensionGroupNumber: PropTypes.number,
    numDimensionGroups: PropTypes.number,
    label: PropTypes.string,
  }),
  frameCount: PropTypes.number,
  currentFrame: PropTypes.number,
  onFrameChange: PropTypes.func,
  showCloseButton: PropTypes.bool,
};

export default CinePlayer;
